/* eslint-disable react-hooks/rules-of-hooks */
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Checkbox, CircularProgress, FormControlLabel, FormGroup, Popover, Typography } from '@mui/material'
import styled from '@emotion/styled'
import { PlaylistAdd } from '@emotion-icons/material/PlaylistAdd'
import { Edit } from '@emotion-icons/boxicons-solid/Edit'
import { Colors, Shapes, Typographies, useTypography } from 'src/components/theme'
import { UserList, UserListCustomArea } from 'src/common-services'
import { useAddUserListItem, useRemoveUserListItem } from 'src/components/user-lists-popover/hooks'
import { TextFormField } from 'src/components/text-from-field'
import { SnackbarMessage } from 'src/components/snackbar-message'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { FlexColumn } from 'src/components/flex-containers'

import * as createUserListModalAtoms from 'src/atoms/user-lists/user-list-modal'
import * as userListsModalAtoms from 'src/atoms/user-lists/user-lists-modal'
import * as userListsPopoverAtoms from 'src/atoms/user-lists/user-lists-popover'
import * as userListsAtoms from 'src/atoms/user-lists'

type CustomAreaDetailsFormValues = {
  customAreaName: string
}

const Container = styled.div<{ width?: number }>`
  width: ${({ width }) => (width ? `${width}px` : '300px')};
  background: ${Colors.bgWhite};
  border: 1px solid ${Colors.border};
  box-shadow: ${Colors.BOX_SHADOW};
  border-radius: ${Shapes.borderRadius}px;
`

const TitleWrapper = styled.div`
  padding: ${Shapes.spacing[2]}px ${Shapes.spacing[4]}px;
  padding-bottom: ${Shapes.spacing[2]}px;
`

const Title = styled.span`
  display: block;
  margin-bottom: 4px;
  color: ${Colors.neutral800};
  ${useTypography(Typographies.title2)}
`

const Description = styled.span`
  display: block;
  color: ${Colors.textMedium};
  ${useTypography(Typographies.textmdm)}
`

const ListWrapper = styled.div`
  padding: ${Shapes.spacing[2]}px ${Shapes.spacing[4]}px;
  padding-top: 0px;
`

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${Shapes.spacing[2]}px;
  padding: ${Shapes.spacing[2]}px;
  border-top: solid 1px ${Colors.borderInput};
`

const FormWrapper = styled.div`
  padding: ${Shapes.spacing[2]}px ${Shapes.spacing[4]}px;
`

const StyledPopover = styled(Popover)`
  & .MuiPopover-paper {
    box-shadow: ${Colors.BOX_SHADOW};
  }
`

const StyledSpinner = styled(CircularProgress)`
  &.MuiCircularProgress-root {
    margin: 11.5px;
  }
`

const StyledCheckbox = styled(Checkbox)`
  &.Mui-checked {
    color: ${Colors.tealDark};
  }
`

type UserListProps = {
  readonly list: UserList
}

const UserListRow: React.FC<UserListProps> = ({ list }) => {
  const [isBusy, setIsBusy] = useState(false)
  const [successSnackbar, setSuccessSnackbar] = useState(false)
  const userListItem = useRecoilValue(userListsAtoms.userListItem)
  const savedIn = useRecoilValue(userListsAtoms.savedIn(userListItem))

  const { mutate: addUserListItem, isSuccess: isAdded } = useAddUserListItem({
    onSuccess: () => setSuccessSnackbar(true),
  })
  const { mutate: removeUserListItem, isSuccess: isRemoved } = useRemoveUserListItem()

  const handleSnackbarClose = () => setSuccessSnackbar(false)

  useEffect(() => {
    if (isAdded || isRemoved) {
      setIsBusy(false)
    }
  }, [isAdded, isRemoved])

  const handleChange = (_: React.SyntheticEvent, checked: boolean) => {
    setIsBusy(true)
    if (checked) {
      addUserListItem(list, userListItem)
    } else {
      const listItem = list.items.find((item) => {
        if (userListItem === null) {
          return false
        }
        if ('googlePlaceId' in item.data && 'googlePlaceId' in userListItem.data) {
          return item.data.googlePlaceId === userListItem.data.googlePlaceId
        }
        if ('locationId' in item.data && 'locationId' in userListItem.data) {
          return item.data.locationId === userListItem.data.locationId
        }
        if (item.type === 'custom-area') {
          if ('center' in item.data && 'center' in userListItem.data) {
            return JSON.stringify(item.data.center) === JSON.stringify(userListItem.data.center)
          }
          if ('coordinates' in item.data && 'coordinates' in userListItem.data) {
            return JSON.stringify(item.data.coordinates) === JSON.stringify(userListItem.data.coordinates)
          }
        }
        return false
      })
      if (listItem?.id) {
        removeUserListItem(list, listItem)
      }
    }
  }

  return (
    <>
      <SnackbarMessage
        open={successSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        label="Successfully saved"
        onClose={handleSnackbarClose}
      />
      <FormControlLabel
        control={
          isBusy ? (
            <StyledSpinner size={14} />
          ) : (
            <StyledCheckbox color="success" checked={savedIn.some((l) => l.id === list.id)} />
          )
        }
        label={<Typography variant="textmdm">{list.name}</Typography>}
        onChange={handleChange}
      />
    </>
  )
}

export const UserListsPopover: React.FC = () => {
  const [anchorElement, setAnchorElement] = useRecoilState(userListsPopoverAtoms.anchorElement)
  const [userListItem, setUserListItem] = useRecoilState(userListsAtoms.userListItem)
  const setPosition = useSetRecoilState(userListsPopoverAtoms.position)
  const position = useRecoilValue(userListsPopoverAtoms.position)
  const userListItemDetails = useRecoilValue(userListsAtoms.userListItemDetails)
  const userLists = useRecoilValue(userListsAtoms.userLists)
  const setCreateUserListModalState = useSetRecoilState(createUserListModalAtoms.state)
  const setUserListsModalOpen = useSetRecoilState(userListsModalAtoms.open)

  const handleFormSubmit = (values: CustomAreaDetailsFormValues) => {
    if (userListItem?.type === 'custom-area') {
      setUserListItem({
        ...userListItem,
        data: { ...userListItem?.data, name: values.customAreaName, description: '' },
      })
    }
  }

  const handleClose = () => {
    reset()
    setAnchorElement(null)
    setPosition({ vertical: 32, horizontal: 0 })
    if (userListItem && userListItem.type === 'custom-area') {
      setUserListItem({ ...userListItem, data: { ...userListItem.data, name: '', description: '' } })
    }
  }

  const handleClickCreate = () => setCreateUserListModalState({})

  const handleClickManage = () => setUserListsModalOpen(true)

  const renderUserListRows = (list: UserList) => <UserListRow key={list.id} list={list} />

  const formMethods = useForm<CustomAreaDetailsFormValues>({
    defaultValues: {
      customAreaName: '',
    },
    mode: 'all',
  })
  const { control, handleSubmit, reset } = formMethods

  if (userListItem?.type === 'custom-area' && !(userListItemDetails as UserListCustomArea)?.name) {
    return (
      <StyledPopover
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        anchorOrigin={position}
        onClose={handleClose}>
        <Container>
          <TitleWrapper>
            <Title>Name your custom area</Title>
          </TitleWrapper>
          <FormWrapper>
            <FormProvider {...formMethods}>
              <form onSubmit={handleSubmit(handleFormSubmit)}>
                <FlexColumn gap={Shapes.spacing[2]}>
                  <TextFormField
                    fullWidth
                    name="customAreaName"
                    placeholder="Custom Area Name"
                    control={control}
                    rules={{
                      required: { value: true, message: 'Custom area name is required' },
                    }}
                  />
                  <ButtonWithIcon ghost type="submit" fullWidth>
                    Continue
                  </ButtonWithIcon>
                </FlexColumn>
              </form>
            </FormProvider>
          </FormWrapper>
        </Container>
      </StyledPopover>
    )
  }

  return (
    <StyledPopover open={Boolean(anchorElement)} anchorEl={anchorElement} anchorOrigin={position} onClose={handleClose}>
      <Container width={300}>
        <TitleWrapper>
          <Title>
            Save {userListItem?.type === 'custom-area' && (userListItem?.data as UserListCustomArea).name} in your
            list(s)
          </Title>
          <Description>Use lists for your portfolios, reports, etc.</Description>
        </TitleWrapper>
        <ListWrapper>
          <FormGroup>{userLists?.map(renderUserListRows)}</FormGroup>
        </ListWrapper>
        <ActionsWrapper>
          <ButtonWithIcon color={Colors.primaryGreen400} ghost onClick={handleClickCreate}>
            <PlaylistAdd size={14} />
            Create new list
          </ButtonWithIcon>
          <ButtonWithIcon ghost onClick={handleClickManage}>
            <Edit size={14} />
            Manage list(s)
          </ButtonWithIcon>
        </ActionsWrapper>
      </Container>
    </StyledPopover>
  )
}
